import { useState } from 'react';
import type { Farmer } from '../../../../types';
import { StatusBadge } from '../../../../components/base/StatusBadge';
import { showToast } from '../../../../components/base/Toast';
import { confirmModal } from '../../../../components/base/ConfirmModal';
import { updateFarmer, addEditHistory, getFarmerKyc, getKycLabel, getDebtsByFarmer, getLimitsByFarmer, getContractsByFarmer } from '../../../../store';
import { formatDate, formatCompactCurrency } from '../../../../utils/format';
import { useStore } from '../../../../hooks/useStore';
import { useAuth } from '../../../../hooks/useAuth';
import { DEBT_STATUS, CONTRACT_STATUS, getCreditScoreColor, getCreditScoreBg, getCreditScoreBorder, getCreditScoreLabel } from '../../../../utils/status';
import { ProfileHistoryPanel } from '../../../../components/feature/ProfileHistoryPanel';
import { FarmerAttachmentsSection } from '../../../../components/feature/FarmerAttachmentsSection';

interface DetailOverviewTabProps {
  farmer: Farmer;
}

export default function DetailOverviewTab({ farmer }: DetailOverviewTabProps) {
  useStore();
  const { user: currentUser } = useAuth();
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<Farmer>({ ...farmer });
  const kyc = getFarmerKyc(farmer);
  const kycLabel = getKycLabel(kyc.kycStatus);
  const debts = getDebtsByFarmer(farmer.id);
  const limits = getLimitsByFarmer(farmer.id);
  const contracts = getContractsByFarmer(farmer.id);

  const totalNo = debts.filter((d) => d.conNo > 0).reduce((s, d) => s + d.conNo, 0);
  const noQuaHan = debts.filter((d) => d.status === DEBT_STATUS.OVERDUE).reduce((s, d) => s + d.conNo, 0);
  const hanMucTong = limits.reduce((s, l) => s + l.hanMuc, 0);
  const daDung = limits.reduce((s, l) => s + l.daSuDung, 0);
  const conLai = hanMucTong - daDung;
  const validContracts = contracts.filter((c) => c.status === CONTRACT_STATUS.ACTIVE).length;
  const isKycVerified = kyc.kycStatus === 'ekyc_verified';
  const scoreColor = getCreditScoreColor(kyc.creditScore);
  const scoreBg = getCreditScoreBg(kyc.creditScore);
  const scoreBorder = getCreditScoreBorder(kyc.creditScore);
  const scoreLabel = getCreditScoreLabel(kyc.creditScore);
  // Once a profile HTX pre-created without an account gets synced to a real
  // farmer account, edit rights move to the farmer — see
  // EkycService.assertProfileEditAllowed. A normally self-registered farmer
  // (createdWithoutAccount unset) is never locked here.
  const isSyncLocked = !!farmer.createdWithoutAccount && !!farmer.userId;

  const handleChange = (field: keyof Farmer, value: string | number) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    const ok = await confirmModal({
      title: 'Xác nhận cập nhật',
      message: 'Bạn có chắc muốn lưu thay đổi thông tin nông hộ?',
      confirmText: 'Lưu thay đổi',
      cancelText: 'Hủy',
      variant: 'primary',
    });
    if (!ok) return;

    const changedFields: (keyof Farmer)[] = ['name', 'phone', 'address', 'tinhThanh', 'huyen', 'xa', 'soThanhVien', 'loaiCay', 'ghiChu', 'htxStatus'];
    changedFields.forEach((field) => {
      const oldVal = String(farmer[field] ?? '');
      const newVal = String(form[field] ?? '');
      if (oldVal !== newVal) {
        addEditHistory({ entityType: 'Farmer', entityId: farmer.id, farmerId: farmer.id, truong: field, giaTriCu: oldVal, giaTriMoi: newVal, ngaySua: new Date().toISOString(), nguoiSua: currentUser?.displayName || 'HTX' });
      }
    });
    try {
      await updateFarmer(farmer.id, { name: form.name, phone: form.phone, address: form.address, tinhThanh: form.tinhThanh, huyen: form.huyen, xa: form.xa, soThanhVien: form.soThanhVien, loaiCay: form.loaiCay, ghiChu: form.ghiChu, htxStatus: form.htxStatus });
      setEditing(false);
      showToast('Đã cập nhật thông tin nông hộ', 'success');
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Cập nhật thất bại', 'error');
    }
  };

  const handleCancel = () => { setForm({ ...farmer }); setEditing(false); };

  const displayFields = [
    { label: 'Mã nông hộ', value: farmer.code || 'Chưa có mã', editable: false },
    { label: 'Họ tên chủ hộ', value: farmer.name, field: 'name' as const },
    { label: 'Số điện thoại', value: farmer.phone, field: 'phone' as const },
    { label: 'Địa chỉ', value: farmer.address, field: 'address' as const },
    { label: 'Tỉnh/thành', value: farmer.tinhThanh, field: 'tinhThanh' as const },
    { label: 'Huyện', value: farmer.huyen, field: 'huyen' as const },
    { label: 'Xã', value: farmer.xa, field: 'xa' as const },
    { label: 'Số thành viên lao động', value: String(farmer.soThanhVien), field: 'soThanhVien' as const, type: 'number' },
    { label: 'Cây trồng chính', value: farmer.loaiCay, field: 'loaiCay' as const },
    { label: 'Diện tích (ha)', value: `${farmer.dienTich} ha`, editable: false },
    { label: 'Ngày bắt đầu hợp tác', value: formatDate(farmer.ngayBatDau), editable: false },
    { label: 'Trạng thái nông hộ', value: <StatusBadge status={farmer.status} />, editable: false },
    { label: 'Trạng thái HTX', value: farmer.htxStatus ? <StatusBadge status={farmer.htxStatus} /> : '-', field: 'htxStatus' as const, select: ['Đang hoạt động', 'Cần chú ý', 'Nguy cơ', 'Tạm ngưng', 'Không đủ điều kiện vay'] },
    { label: 'Ghi chú', value: farmer.ghiChu || '-', field: 'ghiChu' as const },
  ];

  return (
    <div className="space-y-4">
      {/* ── Key indicators strip ── */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 md:gap-3">
        <div className="bg-white rounded-xl border border-slate-200 p-3 text-center">
          <p className="text-xs text-slate-400 mb-1">eKYC</p>
          <StatusBadge status={kycLabel} size="sm" />
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-3 text-center">
          <p className="text-xs text-slate-400 mb-1">Credit score</p>
          {kyc.creditScore ? (
            <span className={`inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full text-xs font-bold ${scoreBg} ${scoreColor} border ${scoreBorder}`}>
              <span className="w-2.5 h-2.5 flex items-center justify-center"><i className="ri-funds-line text-[10px]" /></span>
              {kyc.creditScore}/900
            </span>
          ) : (
            <p className="text-sm text-slate-400">—</p>
          )}
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-3 text-center">
          <p className="text-xs text-slate-400 mb-1">Risk</p>
          {kyc.riskLevel ? <StatusBadge status={kyc.riskLevel} size="sm" /> : <p className="text-xs text-slate-400">—</p>}
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-3 text-center">
          <p className="text-xs text-slate-400 mb-1">Hạn mức còn</p>
          <p className={`text-sm font-bold ${hanMucTong > 0 ? 'text-agrida-700' : 'text-slate-400'}`}>{hanMucTong > 0 ? formatCompactCurrency(conLai) : '—'}</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-3 text-center">
          <p className="text-xs text-slate-400 mb-1">Dư nợ</p>
          <p className={`text-sm font-bold ${totalNo > 0 ? 'text-red-600' : 'text-slate-400'}`}>{totalNo > 0 ? formatCompactCurrency(totalNo) : '0'}</p>
        </div>
      </div>

      {/* ── Alerts ── */}
      {(!isKycVerified || noQuaHan > 0 || (kyc.creditScore != null && kyc.creditScore < 450)) && (
        <div className="space-y-2">
          {!isKycVerified && (
            <div className="p-2.5 rounded-xl bg-amber-50 border border-amber-100 text-xs text-amber-800 flex items-start gap-2">
              <span className="w-4 h-4 inline-flex items-center justify-center text-amber-500 shrink-0 mt-0.5"><i className="ri-shield-user-line" /></span>
              <span>Chưa eKYC — không bắt buộc; HTX có thể làm eKYC hộ để cộng điểm tín nhiệm (cần có trước khi giải ngân)</span>
            </div>
          )}
          {noQuaHan > 0 && (
            <div className="p-2.5 rounded-xl bg-red-50 border border-red-100 text-xs text-red-800 flex items-start gap-2">
              <span className="w-4 h-4 inline-flex items-center justify-center text-red-500 shrink-0 mt-0.5"><i className="ri-close-circle-fill" /></span>
              <span>Nợ quá hạn {formatCompactCurrency(noQuaHan)} — chặn duyệt vay mới</span>
            </div>
          )}
          {kyc.creditScore != null && kyc.creditScore < 450 && (
            <div className="p-2.5 rounded-xl bg-red-50 border border-red-100 text-xs text-red-800 flex items-start gap-2">
              <span className="w-4 h-4 inline-flex items-center justify-center text-red-500 shrink-0 mt-0.5"><i className="ri-alarm-warning-line" /></span>
              <span>Điểm tín dụng Nguy cơ ({kyc.creditScore}/900) — nếu duyệt vay, cần xác nhận ngoại lệ lần 2</span>
            </div>
          )}
        </div>
      )}

      {/* ── Profile card ── */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="h-24 bg-agrida-700 flex items-center justify-center">
          <div className="text-center text-white">
            <div className="w-14 h-14 mx-auto mb-1.5 rounded-full bg-white/20 flex items-center justify-center">
              <i className="ri-user-3-line text-2xl" />
            </div>
            <p className="font-semibold">{farmer.name}</p>
            <p className="text-xs text-agrida-100">{farmer.code}</p>
          </div>
        </div>
        <div className="p-4 md:p-5">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
            <h3 className="text-base font-semibold text-slate-900">Thông tin chi tiết</h3>
            {!editing && !isSyncLocked && (
              <button type="button" onClick={() => setEditing(true)} className="px-3 py-2 rounded-md text-sm font-medium bg-agrida-50 text-agrida-700 hover:bg-agrida-100 transition-colors whitespace-nowrap min-h-[44px]">
                <span className="w-4 h-4 inline-flex items-center justify-center mr-1"><i className="ri-edit-line" /></span>
                Chỉnh sửa hồ sơ
              </button>
            )}
          </div>

          {isSyncLocked && (
            <div className="mb-4 p-2.5 rounded-xl bg-sky-50 border border-sky-100 text-xs text-sky-800 flex items-start gap-2">
              <span className="w-4 h-4 inline-flex items-center justify-center text-sky-500 shrink-0 mt-0.5"><i className="ri-user-3-line" /></span>
              <span>Hồ sơ đã đồng bộ với tài khoản của nông hộ — chỉ nông hộ mới chỉnh sửa được thông tin này, HTX chỉ có thể xem.</span>
            </div>
          )}

          {editing ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {displayFields.map((f, idx) => {
                if (!f.editable && f.editable !== undefined) {
                  return <div key={idx}><p className="text-xs text-slate-400 mb-1">{f.label}</p><div className="text-sm font-medium text-slate-800">{f.value}</div></div>;
                }
                const fieldName = f.field!;
                const val = form[fieldName] ?? '';
                if (f.select) {
                  return (
                    <div key={idx}>
                      <label className="block text-xs text-slate-500 mb-1">{f.label}</label>
                      <select value={val as string} onChange={(e) => handleChange(fieldName, e.target.value)} className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-agrida-500">
                        {f.select.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
                      </select>
                    </div>
                  );
                }
                return (
                  <div key={idx}>
                    <label className="block text-xs text-slate-500 mb-1">{f.label}</label>
                    <input type={f.type || 'text'} value={val} onChange={(e) => handleChange(fieldName, f.type === 'number' ? Number(e.target.value) : e.target.value)} className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-agrida-500" />
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
              {displayFields.map((f, idx) => (
                <div key={idx} className="py-2.5 border-b border-slate-50 last:border-0 sm:border-0 sm:py-0">
                  <p className="text-xs text-slate-500 mb-0.5">{f.label}</p>
                  <div className="text-sm font-medium text-slate-800 break-words">{f.value}</div>
                </div>
              ))}
            </div>
          )}

          {editing && (
            <div className="flex flex-wrap gap-3 justify-end mt-6">
              <button type="button" onClick={handleCancel} className="px-4 py-2 rounded-md text-sm font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 transition-colors whitespace-nowrap min-h-[44px]">Hủy</button>
              <button type="button" onClick={handleSave} className="px-4 py-2 rounded-md text-sm font-medium bg-agrida-700 hover:bg-agrida-800 text-white transition-colors whitespace-nowrap min-h-[44px]">Lưu thay đổi</button>
            </div>
          )}

          <FarmerAttachmentsSection farmer={farmer} htxId={currentUser?.htxId} editable={!isSyncLocked} />

          <div className="mt-4 pt-4 border-t border-slate-100">
            <ProfileHistoryPanel farmerId={farmer.id} />
          </div>
        </div>
      </div>

      {/* ── Quick summary ── */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <div className="bg-white rounded-xl border border-slate-200 p-3">
          <p className="text-xs text-slate-400">HĐ hiệu lực</p>
          <p className="text-sm font-bold text-sky-700">{validContracts}</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-3">
          <p className="text-xs text-slate-400">Nợ quá hạn</p>
          <p className={`text-sm font-bold ${noQuaHan > 0 ? 'text-red-600' : 'text-slate-400'}`}>{noQuaHan > 0 ? formatCompactCurrency(noQuaHan) : '—'}</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-3">
          <p className="text-xs text-slate-400">Đã tất toán</p>
          <p className="text-sm font-bold text-agrida-700">{debts.filter((d) => d.status === DEBT_STATUS.SETTLED).length} khoản</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-3">
          <p className="text-xs text-slate-400">Tổng hạn mức</p>
          <p className="text-sm font-bold text-slate-700">{hanMucTong > 0 ? formatCompactCurrency(hanMucTong) : '—'}</p>
        </div>
      </div>
    </div>
  );
}